/**
 * RoomingByRole
 * ──────────────────────────────────────────────────────────────────────
 * Role-specific Rooming / Patient Flow surface with actionable kanban
 * cards that move patients between phases (Edit 3 + user follow-up).
 *
 * Props:
 *   role:       'front_desk' | 'ma' | 'provider' | 'manager'
 *   items:      [{ id, name, type, provider, providerId, time, waitMin,
 *                   status: PHASE_ID, roomName? }]
 *                  PHASE_ID ∈ 'scheduled' | 'arrived' | 'waiting_room'
 *                              | 'in_exam' | 'with_provider' | 'ready_checkout'
 *   rooms:      (optional) [{ id, name, status, patient, provider, timeMin, type }]
 *                  Still used for the Manager ops strip and MA room-picker hint.
 *   providers:  [{ id, name, behindMin, nextPatient, status, room }]
 *   alerts:     [{ id, type, text, color }]
 *   providerId: (optional) filters the Provider view to their patients.
 *   onMoveTo(item, toStatus)  — caller applies the state transition.
 *   onRoomPatient(item, availableRooms) — MA row action (legacy path).
 *
 * Pure UI; no banned literals live here. Role hooks gate:
 *   FD:        cannot push into in_exam / with_provider (provider's jobs).
 *   MA:        cannot touch scheduled / ready_checkout (front-desk jobs).
 *   Provider:  entirely read-only.
 *   Manager:   full access including overrides.
 */
import { useState } from 'react';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import {
  Clock, AlertTriangle, Sparkles, Stethoscope, FileText, ChevronDown, MoveRight,
} from 'lucide-react';

/* Canonical phase list used across roles. */
export const PHASES = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'arrived', label: 'Arrived' },
  { id: 'waiting_room', label: 'Waiting Room' },
  { id: 'in_exam', label: 'In Exam Room' },
  { id: 'with_provider', label: 'With Provider' },
  { id: 'ready_checkout', label: 'Ready for Checkout' },
];

const PHASE_LABEL = Object.fromEntries(PHASES.map((p) => [p.id, p.label]));

/* Per-role column whitelist (drives both view layout and allowed drops). */
const COLUMNS_BY_ROLE = {
  front_desk: ['scheduled', 'arrived', 'waiting_room', 'in_exam', 'with_provider', 'ready_checkout'],
  ma: ['arrived', 'waiting_room', 'in_exam', 'with_provider'],
  manager: ['scheduled', 'arrived', 'waiting_room', 'in_exam', 'with_provider', 'ready_checkout'],
  provider: [],
};

/* Per-role write rule: is this role allowed to move FROM / TO a given phase? */
const WRITABLE_BY_ROLE = {
  front_desk: new Set(['scheduled', 'arrived', 'waiting_room', 'ready_checkout']),
  ma: new Set(['arrived', 'waiting_room', 'in_exam', 'with_provider']),
  manager: new Set(['scheduled', 'arrived', 'waiting_room', 'in_exam', 'with_provider', 'ready_checkout']),
  provider: new Set(),
};

const canMove = (role, fromStatus, toStatus) => {
  const w = WRITABLE_BY_ROLE[role];
  if (!w || w.size === 0) return false;
  return w.has(fromStatus) && w.has(toStatus);
};

/* ── Kanban card ── */
const FlowCard = ({ item, role, onMoveTo, extraActionLabel, onExtraAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const writable = canMove(role, item.status, item.status); // is origin writable?
  const destinations = writable
    ? PHASES.filter((p) => p.id !== item.status && WRITABLE_BY_ROLE[role].has(p.id))
    : [];
  const isReadOnly = destinations.length === 0;

  return (
    <div
      className={cn(
        'p-2 rounded-md border border-slate-200 bg-white space-y-0.5',
        isReadOnly && 'opacity-90'
      )}
      data-testid={`flow-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium text-slate-800 truncate">{item.name}</div>
          <div className="text-[9px] text-slate-500 truncate">
            {item.time && `${item.time} · `}{item.type || ''}
          </div>
          {item.provider && (
            <div className="text-[9px] text-slate-400 truncate">{item.provider}</div>
          )}
          {item.roomName && (
            <div className="text-[9px] text-blue-600 truncate">{item.roomName}</div>
          )}
          {typeof item.waitMin === 'number' && (
            <div
              className={cn(
                'text-[9px] font-medium',
                item.waitMin > 15 ? 'text-red-600' : 'text-amber-600'
              )}
            >
              Waiting {item.waitMin}m
            </div>
          )}
        </div>
      </div>

      {extraActionLabel && onExtraAction && (
        <Button
          size="sm"
          className="h-5 text-[9px] w-full bg-[#111] hover:bg-[#222] mt-1"
          onClick={onExtraAction}
          data-testid={`flow-card-${item.id}-extra`}
        >
          {extraActionLabel}
        </Button>
      )}

      {!isReadOnly && (
        <div className="relative mt-1" data-testid={`flow-card-${item.id}-move`}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-1 px-1.5 py-1 rounded text-[9px] bg-slate-50 hover:bg-slate-100 text-slate-700"
            style={{ border: '0.5px solid #e5e7eb', cursor: 'pointer' }}
            data-testid={`flow-card-${item.id}-move-btn`}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
          >
            <span className="flex items-center gap-1 truncate">
              <MoveRight className="h-2.5 w-2.5" /> Move to…
            </span>
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
          {menuOpen && (
            <div
              className="absolute z-20 left-0 right-0 mt-0.5 rounded-md border border-slate-200 bg-white shadow-lg py-1"
              role="listbox"
              data-testid={`flow-card-${item.id}-move-menu`}
            >
              {destinations.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setMenuOpen(false);
                    onMoveTo?.(item, p.id);
                  }}
                  className="w-full text-left px-2 py-1 text-[10px] text-slate-700 hover:bg-slate-50"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  data-testid={`flow-card-${item.id}-move-${p.id}`}
                  role="option"
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isReadOnly && item.status === 'in_exam' && role === 'front_desk' && (
        <div className="text-[8px] text-slate-400 italic mt-1">Read-only (MA/Provider)</div>
      )}
    </div>
  );
};

/* ── HTML5 drag-and-drop helpers ── */
const useDnD = (role, onMoveTo) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  return {
    dragProps: (item) => ({
      draggable: WRITABLE_BY_ROLE[role]?.has(item.status),
      onDragStart: (e) => {
        setDragId(item.id);
        try { e.dataTransfer.setData('text/plain', item.id); } catch (_) {}
      },
      onDragEnd: () => { setDragId(null); setOverCol(null); },
    }),
    colProps: (colId, items) => ({
      onDragOver: (e) => {
        // Only allow drop if role can write to this column
        if (WRITABLE_BY_ROLE[role]?.has(colId)) {
          e.preventDefault();
          setOverCol(colId);
        }
      },
      onDragLeave: () => setOverCol(null),
      onDrop: (e) => {
        e.preventDefault();
        const id = (() => { try { return e.dataTransfer.getData('text/plain'); } catch (_) { return dragId; } })();
        const it = items.find((x) => x.id === id);
        if (it && it.status !== colId && canMove(role, it.status, colId)) {
          onMoveTo?.(it, colId);
        }
        setOverCol(null);
        setDragId(null);
      },
    }),
    overCol,
    dragId,
  };
};

/* ── Shared kanban board ── */
const KanbanBoard = ({
  role,
  items,
  columns,
  onMoveTo,
  testIdPrefix,
  extraActionFor, // (item, col) => { label, onClick } | null
  colLabelSuffix, // (col) => React.Node | null
}) => {
  const dnd = useDnD(role, onMoveTo);
  const byCol = (colId) => items.filter((it) => it.status === colId);
  return (
    <div className={cn('grid gap-2', columns.length >= 6 ? 'grid-cols-6' : 'grid-cols-4')}>
      {columns.map((colId) => {
        const col = PHASES.find((p) => p.id === colId);
        const colItems = byCol(colId);
        const colReadOnly = !WRITABLE_BY_ROLE[role]?.has(colId);
        return (
          <div
            key={colId}
            {...dnd.colProps(colId, items)}
            className={cn(
              'rounded-lg border border-slate-200 bg-slate-50/60 p-2 min-h-[220px] transition-colors',
              colReadOnly && 'bg-slate-50',
              dnd.overCol === colId && 'ring-2 ring-violet-300 bg-violet-50/40'
            )}
            data-testid={`${testIdPrefix}-col-${colId}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-slate-700">
                {col?.label}
                {colLabelSuffix && colLabelSuffix(colId)}
              </span>
              {colReadOnly ? (
                <span className="text-[8px] text-slate-400">Read-only</span>
              ) : (
                <span className="text-[8px] text-slate-400">{colItems.length}</span>
              )}
            </div>
            <div className="space-y-1.5">
              {colItems.map((it) => {
                const extra = extraActionFor ? extraActionFor(it, colId) : null;
                return (
                  <div key={it.id} {...dnd.dragProps(it)}>
                    <FlowCard
                      item={it}
                      role={role}
                      onMoveTo={onMoveTo}
                      extraActionLabel={extra?.label}
                      onExtraAction={extra?.onClick}
                    />
                  </div>
                );
              })}
              {colItems.length === 0 && (
                <div className="text-[9px] text-slate-300 italic px-1 py-4 text-center">
                  Drop here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Shared alert strip ── */
const AlertStrip = ({ alerts }) => (
  <div
    className="rounded-lg border border-slate-200 bg-white p-3 space-y-1.5"
    data-testid="rooming-alert-strip"
  >
    <div className="flex items-center gap-1.5">
      <Sparkles className="h-3.5 w-3.5 text-violet-500" />
      <span className="text-xs font-semibold text-slate-800">AI Flow Intelligence</span>
      <Badge className="text-[9px] border-0 bg-slate-100 text-slate-500">{alerts.length} active</Badge>
    </div>
    {alerts.map((a) => (
      <div
        key={a.id}
        className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-100"
        data-testid={`rooming-alert-${a.id}`}
      >
        <AlertTriangle className="h-3 w-3 shrink-0" style={{ color: a.color || '#f59e0b' }} />
        <span className="flex-1 text-[11px] text-slate-700">{a.text}</span>
      </div>
    ))}
  </div>
);

/* ── Role: Front Desk ── */
const FrontDeskView = ({ items, alerts, onMoveTo }) => (
  <div className="space-y-3" data-testid="rooming-fd">
    {alerts?.length > 0 && <AlertStrip alerts={alerts} />}
    <KanbanBoard
      role="front_desk"
      items={items}
      columns={COLUMNS_BY_ROLE.front_desk}
      onMoveTo={onMoveTo}
      testIdPrefix="fd"
    />
  </div>
);

/* ── Role: MA ── */
const MAView = ({ items, alerts, availableRooms, onMoveTo, onRoomPatient }) => {
  const maAlerts = (alerts || []).filter((a) => a.type !== 'lag' && a.type !== 'financial');
  const extraActionFor = (item, colId) => {
    if (colId === 'arrived' && availableRooms.length > 0) {
      return { label: 'Room patient', onClick: () => onRoomPatient?.(item, availableRooms) };
    }
    return null;
  };
  return (
    <div className="space-y-3" data-testid="rooming-ma">
      {maAlerts.length > 0 && <AlertStrip alerts={maAlerts} />}
      <KanbanBoard
        role="ma"
        items={items}
        columns={COLUMNS_BY_ROLE.ma}
        onMoveTo={onMoveTo}
        testIdPrefix="ma"
        extraActionFor={extraActionFor}
      />
    </div>
  );
};

/* ── Role: Provider (focused list, no kanban) ── */
const ProviderView = ({ items, providerId }) => {
  const mine = items
    .filter((it) => !providerId || it.providerId === providerId || !it.providerId)
    .filter((it) => ['in_exam', 'with_provider', 'waiting_room', 'arrived'].includes(it.status))
    .map((it) => ({
      id: it.id,
      location: it.roomName || PHASE_LABEL[it.status],
      name: it.name,
      type: it.type,
      wait: typeof it.waitMin === 'number' ? `${it.waitMin} min` : it.time || '',
      status: it.status,
      hasContingency: !!it.hasContingency,
    }))
    .sort((a, b) => (parseInt(b.wait) || 0) - (parseInt(a.wait) || 0));

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="rooming-provider">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Stethoscope className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">Your patients</span>
      </div>
      {mine.length === 0 && (
        <div className="p-6 text-center text-[11px] text-slate-500">No assigned patients.</div>
      )}
      <div className="divide-y divide-slate-100">
        {mine.map((p) => (
          <div
            key={p.id}
            className="px-4 py-2.5 flex items-center gap-3"
            data-testid={`provider-row-${p.id}`}
          >
            <div
              className={cn(
                'h-2 w-2 rounded-full shrink-0',
                ['in_exam', 'with_provider'].includes(p.status) ? 'bg-blue-500' : 'bg-amber-500'
              )}
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-800 truncate flex items-center gap-1.5">
                <span className="truncate">{p.location} · {p.name}{p.type ? ` · ${p.type}` : ''}</span>
                {p.hasContingency && (
                  <span
                    className="text-[8px] font-semibold px-1 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0"
                    data-testid={`provider-ob-pill-${p.id}`}
                    title="Overbook contingency active"
                  >
                    OB
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500">Waiting {p.wait}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px] gap-1"
              data-testid={`provider-chart-${p.id}`}
            >
              <FileText className="h-2.5 w-2.5" /> View chart
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Role: Manager ── */
const ManagerView = ({ items, rooms, providers, alerts, onMoveTo }) => (
  <div className="space-y-3" data-testid="rooming-manager">
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="manager-ops-strip">
      <div className="grid grid-cols-3 gap-4 text-[11px]">
        <div>
          <div className="text-[9px] uppercase text-slate-400 font-semibold mb-1">Rooms</div>
          <div className="flex flex-wrap gap-1.5">
            {rooms.map((r) => (
              <span
                key={r.id}
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]',
                  r.status === 'available'
                    ? 'bg-emerald-50 text-emerald-700'
                    : r.status === 'cleaning'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-blue-50 text-blue-700'
                )}
              >
                {r.name}{r.patient ? ` · ${r.patient.split(' ')[0]} · ${r.timeMin}m` : ' · available'}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-400 font-semibold mb-1">Providers</div>
          <div className="space-y-0.5">
            {providers.map((pr) => (
              <div key={pr.id} className="text-[10px] text-slate-700">
                {pr.name}
                {pr.behindMin > 0
                  ? <span className="text-amber-600 ml-1">running {pr.behindMin}m late</span>
                  : <span className="text-slate-400 ml-1">on time</span>}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-400 font-semibold mb-1">Wait alerts</div>
          {items.filter((q) => (q.waitMin || 0) > 15).length === 0 ? (
            <div className="text-[10px] text-slate-500">No critical waits.</div>
          ) : (
            items.filter((q) => (q.waitMin || 0) > 15).map((q) => (
              <div key={q.id} className="text-[10px] text-red-600">
                {q.name} — {q.waitMin}m
              </div>
            ))
          )}
        </div>
      </div>
    </div>

    {alerts?.length > 0 && <AlertStrip alerts={alerts} />}

    <KanbanBoard
      role="manager"
      items={items}
      columns={COLUMNS_BY_ROLE.manager}
      onMoveTo={onMoveTo}
      testIdPrefix="mgr"
    />
  </div>
);

const RoomingByRole = ({
  role,
  items = [],
  rooms = [],
  providers = [],
  alerts = [],
  providerId = null,
  onMoveTo,
  onRoomPatient,
}) => {
  const inner = (() => {
    if (role === 'manager') {
      return (
        <ManagerView
          items={items}
          rooms={rooms}
          providers={providers}
          alerts={alerts}
          onMoveTo={onMoveTo}
        />
      );
    }
    if (role === 'provider') {
      return <ProviderView items={items} providerId={providerId} />;
    }
    if (role === 'ma') {
      const availableRooms = rooms.filter((r) => r.status === 'available');
      return (
        <MAView
          items={items}
          alerts={alerts}
          availableRooms={availableRooms}
          onMoveTo={onMoveTo}
          onRoomPatient={onRoomPatient}
        />
      );
    }
    return <FrontDeskView items={items} alerts={alerts} onMoveTo={onMoveTo} />;
  })();

  return <div data-testid="rooming-by-role">{inner}</div>;
};

export default RoomingByRole;
